import { useState, useEffect, useContext } from "react";
import Image from "next/image";
import { useAtom, useSetAtom } from "jotai";
import clsx from "clsx";
import axios from "axios";
import React from "react";

import { AuthContext } from "contexts/auth";
import { CREW_END_POINTS } from "@/constants/crew/endPoints";
import { rotateDegreeAtom } from "@/states/rotateDegreeAtom";
import ButtonV2 from "components/shared/buttonV2";
import closeModalIcon from "assets/icons/close-modal.svg";
import rotateIcon from "assets/icons/rotate.svg";
import { Photos } from "@/types/common/photos";
import { photosAtom } from "@/states/photosAtom";
import { currentSliderIndexAtom } from "@/states/currentSliderIndex";
import { ReportContext } from "@/contexts/report";
import { showKeepRotateDegreeModalAtom } from "@/states/common/showKeepRotateDegreeModalAtom";

import styles from "./index.module.scss";

type PhotoModalProps = {
  show: any; // FIXME: string許容しているようなのでbooleanに統一する
  canRotateImage?: boolean;
  setShow: (show: any) => void;
  showClose?: boolean;
  title?: string;
  photos?: Photos[];
  setSelectedPhotos?: React.Dispatch<React.SetStateAction<Photos[]>>;
  children: React.ReactNode;
};

const PhotoModal: React.FC<PhotoModalProps> = (props) => {
  const [rotateDegree, setRotateDegree] = useAtom(rotateDegreeAtom);
  const [rotateAnimation, setRotateAnimation] = useState(false);
  const { token } = useContext(AuthContext);
  const setPhotos = useSetAtom(photosAtom);
  const setShowKeepRotateDegreeModal = useSetAtom(
    showKeepRotateDegreeModalAtom
  ); //写真回転未保存確認モーダル表示/非表示
  const [currentSlider] = useAtom(currentSliderIndexAtom);
  const {
    setFinalPhotos,
    setFinalProblemPhotos,
    setFinalCommentPhotos,
    setOtherReportPhotos
  } = useContext(ReportContext);

  // ボタンクリックで発火するrotateDegree更新用関数
  // 0, -90, -180, -270の4パターン
  const handleRotateDegree = () => {
    setRotateDegree((prev) =>
      prev.map((degree, index) =>
        index === currentSlider ? degree - 90 : degree
      )
    );
  };

  const hasRotateDegree = rotateDegree.some((degree) => degree !== 0);

  // 画像回転させる時間を制御するための関数
  const handleRotateAnimation = () => {
    setRotateAnimation(true);
    setTimeout(() => {
      setRotateAnimation(false);
    }, 300);
  };

  // 画像のidと回転角度をpostする関数
  const handleRotate = async (targetPhotos: Photos[] | undefined) => {
    if (!targetPhotos) return;

    const postData: any = targetPhotos.map((photo, index) => {
      return {
        id: photo.id,
        degree: rotateDegree[index]
      };
    }); //pathがあるものの写真IDと回転角度をセット

    // postが完了したらrotateDegreeを初期値に戻す
    await axios
      .post(
        `${CREW_END_POINTS.REPORT.ROTATE_PHOTOS}`,
        { photos: postData },
        token
      )
      .then((res) => {
        // photosのphotoGroupが一致していて、さらにphotoのidが一致するものだけをres.data[0]で更新する
        const updatedPhotoDataArr = res.data;

        // 全ての更新された写真データを使用して状態を更新
        const updatePhotosState = (prevPhotos: any) => {
          return prevPhotos.map((photoGroup: any) => ({
            ...photoGroup,
            photos: photoGroup.photos?.map((photo: Photos) => {
              const updatedPhotoData = updatedPhotoDataArr.find(
                (uPhoto: Photos) => uPhoto.id === photo.id
              );
              return updatedPhotoData
                ? { ...photo, ...updatedPhotoData }
                : photo;
            })
          }));
        };

        setPhotos(updatePhotosState);
        setFinalCommentPhotos(updatePhotosState);
        setFinalProblemPhotos(updatePhotosState);
        setOtherReportPhotos(updatePhotosState);
        setFinalPhotos(updatePhotosState);
      })
      .catch((err) => {
        console.error("postに失敗しました", err);
      });
    // 現在の回転角度を初期値に戻す
    // [0, -90, -180, -270, -180]のように複数配列を持っているので全て0に戻す
    setRotateDegree((prev) => {
      return prev.map(() => 0);
    });
  };

  // props.photosの数だけrotateDegreeAtomを保持する
  useEffect(() => {
    setRotateDegree(Array(props.photos?.length).fill(0));
  }, [props.photos]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        props.setShow(false);
        e.preventDefault();
      }
    };

    window.addEventListener("keydown", onKeyDown, false);
    return () => {
      window.removeEventListener("keydown", onKeyDown, false);
    };
  }, []);

  return (
    <>
      {props.show && (
        <div
          className={styles.overlay}
          onClick={() => {
            // 角度保存前に消そうとした時は確認用のモーダルを出す
            if (hasRotateDegree) {
              setShowKeepRotateDegreeModal(true);
              return;
            }
            if (props.showClose) {
              props.setShow(false);
            }
          }}
        >
          {props.showClose && (
            <div
              className={styles.close}
              onClick={() => {
                // 角度保存前に消そうとした時は確認用のモーダルを出す
                if (hasRotateDegree) {
                  setShowKeepRotateDegreeModal(true);
                  return;
                }
                props.setShow(false);
              }}
            >
              <Image src={closeModalIcon} alt="閉じる" width={18} height={18} />
            </div>
          )}
          <div
            className={styles.container}
            onClick={(e) => e.stopPropagation()}
          >
            {props.title && <div className={styles.title}>{props.title}</div>}
            {props.children}
          </div>
          {/* 有効かどうかをbooleanで制御する */}
          {props.canRotateImage && (
            <>
              <button
                className={clsx(
                  "absolute bottom-[8.2rem] flex h-48 w-48 items-center justify-center rounded-full bg-[#232323] opacity-60 active:scale-105",
                  rotateAnimation && "animate-spin-reverse"
                )}
                onClick={(e) => {
                  handleRotateDegree();
                  handleRotateAnimation();
                  e.stopPropagation();
                }}
                disabled={rotateAnimation}
              >
                <Image
                  src={rotateIcon}
                  alt=""
                  width={26}
                  height={29}
                  className="pb-[0.3rem]"
                />
              </button>
              <div className="absolute bottom-40 w-full items-center px-16">
                <div className="flex w-full justify-center">
                  <ButtonV2
                    variant={hasRotateDegree ? "primary" : "neutral"}
                    layout="fullWidth"
                    disabled={!hasRotateDegree}
                    click={(e: React.MouseEvent) => {
                      handleRotate(props.photos);
                      props.setShow(false);
                      e.stopPropagation();
                    }}
                  >
                    保存する
                  </ButtonV2>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default PhotoModal;
